/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */

import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Text from '@atlaskit/primitives/text';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Inline from '@atlaskit/primitives/inline';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Pressable from '@atlaskit/primitives/pressable';
import { token } from '@atlaskit/tokens';

import { useSideNavigation } from '@confluence/page-layout-side-navigation-context/entry-points/SideNavigationContext.ts';

export const ScrollToTopButton = ({
	showExCoMessaging = false,
}: {
	showExCoMessaging?: boolean | undefined;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { handleScrollToTop } = useSideNavigation();

	const onClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'QuickAccess',
				source: 'spaceNavigation',
				data: {
					navigationLayer: 'global',
					navigationContainer: 'sidebar',
					navVersion: '4',
				},
			},
		}).fire();
		handleScrollToTop();
	};

	return (
		<>
			<Pressable
				xcss={[pressableStyles, stickyWrapper, showExCoMessaging && additionalOffset]}
				id="scroll-to-top-button"
				onClick={onClick}
				style={{ borderColor: 'transparent' }}
			>
				<Inline space="space.050" alignInline="center" alignBlock="center">
					<ArrowUpIcon label="" color={token('color.icon.subtle')} size="small" />
					<Text align="center" size="small" color="color.text.subtle">
						<FormattedMessage {...i18n.scrollToTopButtonLabel} />
					</Text>
				</Inline>
			</Pressable>
			<Box xcss={offsetElement} />
		</>
	);
};

const offsetElement = xcss({
	//this element keeps the spacing from he negative top margin tha hides the button
	height: '17px',
	width: `100%`,
	display: 'flex',
});

const additionalOffset = xcss({
	top: `76px`,
});

const stickyWrapper = xcss({
	zIndex: 'card', // Needs to be at least z-index 2 to appear on top of Content and Shortcuts
	display: 'flex',
	position: 'sticky',
	top: `62px`,
	// @ts-ignore not assignable to type 'MarginSpace | GlobalValue | undefined'
	marginTop: `-50px`, //this hides the button underneath the space header until the ser scrolls down
	marginLeft: `auto`,
	marginRight: 'auto',
});

const pressableStyles = xcss({
	backgroundColor: 'elevation.surface',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderRadius: 'radius.full',
	paddingInline: 'space.100',
	alignItems: 'center',
	justifyContent: 'center',
	width: '125px',
	height: '32px',
	flexShrink: '0',
	boxShadow: 'elevation.shadow.overlay',
	transition: token('motion.button.hovered'),

	':hover': {
		backgroundColor: 'elevation.surface.hovered',
	},
	':active': {
		backgroundColor: 'elevation.surface.pressed',
		transition: token('motion.button.pressed'),
	},
	':focus': {
		outline: 'color.border.focused',
	},
});

const i18n = defineMessages({
	scrollToTopButtonLabel: {
		id: 'side-navigation.global.scroll-to-top.button.label',
		defaultMessage: 'Back to top',
		description:
			'Label text for a button indicating it scrolls to the top of the navigation to view menu items.',
	},
});
